import type { SpriteKey } from '../../types';

export const ACCENT = '#86d46f';
export const ACCENT_GLOW = 'rgba(134,212,111,0.55)';

/** Design's soft-glow helper: a #rrggbb hex → same colour at 0.30 alpha. */
export function soft(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},0.30)`;
}

export interface Agent {
  id: SpriteKey;
  name: string;
  role: string;
  desc: string;
  /** Legible label tint derived from the agent's outfit colour. */
  color: string;
}

// Real 10 dwellers (display name + role + verbatim tagline from src/data/dwellers.ts).
// Sprites are rendered from the real procedural pixel art (pixelDweller.ts).
export const AGENTS: Agent[] = [
  { id: 'overseer',     name: 'The Overseer',     role: 'ORCHESTRATOR',       desc: 'Every vault needs a plan. I am the plan.',            color: '#5aa0e8' },
  { id: 'elder',        name: 'The Elder',        role: 'STRATEGY COACH',     desc: "You don't need answers. You need sharper questions.", color: '#d0a040' },
  { id: 'scout',        name: 'The Scout',        role: 'MARKET RESEARCH',    desc: 'The wasteland speaks. I listen.',                     color: '#4ecb77' },
  { id: 'investigator', name: 'The Investigator', role: 'CUSTOMER DISCOVERY', desc: "I don't ask what you think. I ask what you did.",     color: '#b07cc6' },
  { id: 'scribe',       name: 'The Scribe',       role: 'MEMORY',             desc: "Your memory fails. Mine doesn't.",                    color: '#8fa8b8' },
  { id: 'analyst',      name: 'The Analyst',      role: 'DATA & KPIS',        desc: "Vibes lie. Numbers don't.",                           color: '#26d0e4' },
  { id: 'auditor',      name: 'The Auditor',      role: 'QUALITY CONTROL',    desc: 'Trust nothing. Verify everything.',                   color: '#e06060' },
  { id: 'tinkerer',     name: 'The Tinkerer',     role: 'MVP ENGINEER',       desc: 'Code is just words until it runs.',                   color: '#ff9c52' },
  { id: 'designer',     name: 'The Designer',     role: 'PRODUCT & UX',       desc: 'Function dressed in form.',                           color: '#eed45d' },
  { id: 'caravaneer',   name: 'The Caravaneer',   role: 'OUTREACH & SALES',   desc: 'Replies, not noise. Always.',                         color: '#e08a4a' },
];

// ---------------------------------------------------------------------------
// THE LOOP — the story band under the hero.
//
// Read as a terminal boot sequence: the bunker printing its diagnosis of the
// founder before it offers the cure. Deliberately emotional and deliberately
// free of numbers — the filesystem proof (20 interviews) lands two sections
// down on the roadmap, where it can't deflate the closing line.
// ---------------------------------------------------------------------------
export type StoryTone = 'slam' | 'body' | 'quote' | 'beat' | 'punch' | 'finale';
export interface StoryBlock { tone: StoryTone; lines: string[]; }

export const STORY: StoryBlock[] = [
  { tone: 'slam', lines: ['0 USERS.', '0 REVENUE.'] },
  {
    tone: 'body',
    lines: [
      'Not because the idea is bad.',
      'Because nobody outside your laptop has ever touched it.',
    ],
  },
  { tone: 'quote', lines: ['“I’M TWO WEEKS AWAY.”'] },
  { tone: 'body', lines: ['You’ve been two weeks away for four months. ☠'] },
  { tone: 'beat', lines: ['Polishing in private isn’t progress.'] },
  { tone: 'punch', lines: ['YOU DON’T NEED MORE TIME.'] },
  {
    tone: 'finale',
    lines: [
      'YOU NEED A CREW AND A WORKFLOW',
      'THAT WON’T LET YOU STOP UNTIL YOU’VE SHIPPED',
      'THE ONE THAT CHANGES YOUR LIFE.',
    ],
  },
];

export interface Era { n: string; title: string; sub: string; boss: string; locked?: string; }
// Plain-English restatement of the 6 real eras (see CLAUDE.md era table) for
// a founder skimming the landing page — not the internal era names verbatim.
//
// `boss` is the gate's hardest *countable* criterion, copied from
// server/stressTests/era[0-5].ts. Vague labels ("SOLUTION FIT") were replaced
// by the numbers the gate actually counts: the number is the proof.
export const ERAS: Era[] = [
  { n: '01', title: 'IDEA',      sub: 'Find a problem worth solving.',  boss: 'THESIS FILED' },
  { n: '02', title: 'RESEARCH',  sub: 'Map the market and your wedge.', boss: 'BEACHHEAD CHOSEN' },
  { n: '03', title: 'DISCOVERY', sub: 'Talk to real customers.',        boss: '20 INTERVIEWS FILED' },
  { n: '04', title: 'BUILD',     sub: 'Ship a lean, honest MVP.',       boss: 'MVP TESTED WITH 5', locked: 'LOCKED UNTIL 03 CLEARS' },
  { n: '05', title: 'SELL',      sub: 'Land your first paying users.',  boss: '1 PAYING CUSTOMER' },
  { n: '06', title: 'SCALE',     sub: 'Grow the machine that grows.',   boss: 'PMF SIGNALS' },
];

export interface WhyCard { icon: string; title: string; desc: string; }
// The pitch: problem it solves, who it's for, why an app, why a game —
// in that order, since you can't sell the format before the need.
// One sentence each: the story band above already did the persuading, so
// these are the receipts, not the argument.
export const WHY_CARDS: WhyCard[] = [
  {
    icon: '⚠',
    title: 'THE PROBLEM',
    desc: 'Startups rarely die of a bad idea. They die alone — nobody to catch the blind spot, nobody to make you stop building.',
  },
  {
    icon: '◉',
    title: "WHO IT'S FOR",
    desc: "Solo and small-team founders, technical or not, who want a cofounder's discipline without giving up equity.",
  },
  {
    icon: '⚙',
    title: 'WHY AN APP, NOT ADVICE',
    desc: 'Advice tells you what to do. These agents do it with you — market maps, interview scripts, MVP code, outreach drafts.',
  },
  {
    icon: '▲',
    title: 'WHY A GAME, NOT A COURSE',
    desc: 'Courses get abandoned. Games get finished. And every gate checks real files, not vibes.',
  },
];

export interface Step { n: string; title: string; desc: string; screen: string; glow: string; }
// The core loop, in the order a founder experiences it. Each step names the
// concrete action and its concrete output — no metaphor without a mechanic.
export const STEPS: Step[] = [
  {
    n: '01',
    title: 'TELL THE OVERSEER YOUR IDEA',
    desc: 'Even a rough one. It comes back as a short list of missions, each assigned to the agent built for that job.',
    screen: "OVERSEER'S OFFICE",
    glow: '#3fa0e0',
  },
  {
    n: '02',
    title: 'AGENTS DELIVER, YOU DECIDE',
    desc: 'Every mission returns something real — a market map, an interview script, working code. You read it, edit it, approve it.',
    screen: 'COMM ROOM',
    glow: '#6ede8a',
  },
  {
    n: '03',
    title: 'CLEAR THE GATE, UNLOCK THE FLOOR',
    desc: 'Each era ends at a checkpoint that inspects your actual files. Pass it and the next floor comes online.',
    screen: 'QC LAB',
    glow: '#e05050',
  },
];

export interface VaultFile { path: string; note: string; agent: string; }
// Real deliverable paths, copied from the `deliverables` fields in
// src/data/quests.ts — the same paths the era gates check on disk. Keep them
// verbatim: the whole point of this section is that the files are real.
// Eight, not sixteen: one per beat of the journey, so the list reads as an
// arc (identity → market → segment → customers → decision → build → price →
// sell) instead of a directory dump.
export const VAULT_FILES: VaultFile[] = [
  { path: 'scribe/thesis.md',                 note: 'the problem you are uniquely placed to solve', agent: 'SCRIBE' },
  { path: 'scout/competitors.md',             note: 'the landscape and the white space',            agent: 'SCOUT' },
  { path: 'scribe/beachhead.md',              note: 'the one segment you attack first',             agent: 'SCRIBE' },
  { path: 'investigator/interviews/*.md',     note: 'every customer conversation, filed',           agent: 'INVESTIGATOR' },
  { path: 'scribe/opportunity-memo.md',       note: 'the 12-section go / no-go memo',               agent: 'SCRIBE' },
  { path: 'tinkerer/mvp-spec.md',             note: 'the smallest thing worth building',            agent: 'TINKERER' },
  { path: 'analyst/pricing.md',               note: 'value-based, not cost-plus',                   agent: 'ANALYST' },
  { path: 'caravaneer/sales-playbook.md',     note: 'the outreach that got replies',                agent: 'CARAVANEER' },
];

export interface Faq { q: string; a: string; by: string; id: SpriteKey; }
// Objection handling, answered in each dweller's own voice — the objections
// are the ones a sceptical founder actually raises, in the order they raise
// them. Four, not six: the course/accelerator and bad-idea objections are
// already answered by the story band and the roadmap.
export const FAQ: Faq[] = [
  {
    q: 'Isn’t this just ChatGPT with a skin?',
    a: 'ChatGPT agrees with you. I don’t. Every era ends at a gate that reads the actual files in your vault and blocks you if the work isn’t there. No amount of clever prompting talks its way past a filesystem check.',
    by: 'THE AUDITOR', id: 'auditor',
  },
  {
    q: 'I’ve already been building for 8 months. Do I start over?',
    a: 'No. Tell me what exists and I route you to the era that matches — the gates check for evidence, not for time served. If the interviews are already done, you clear Era 3 in a day. If they aren’t, that is the eight months talking.',
    by: 'THE OVERSEER', id: 'overseer',
  },
  {
    q: 'I’m not technical. Can I still use it?',
    a: 'Most of the work is thinking, talking to customers and writing. When something needs to be built, I build it and you review it. Bring judgement; I’ll bring the keyboard.',
    by: 'THE TINKERER', id: 'tinkerer',
  },
  {
    q: 'What do I actually own at the end?',
    a: 'Files. Real ones — market maps, interview transcripts, a business model canvas, pricing analysis, pitch decks. Plain markdown and PDFs in your own vault. Cancel tomorrow and the work is still yours.',
    by: 'THE SCRIBE', id: 'scribe',
  },
];

// The founder section. Everything here must stay literally true — it is the
// one claim on the page that rests on the founder's own credibility.
export const FOUNDER = {
  name: 'Nicolas Dellagiovanna',
  role: 'SOLO FOUNDER — USER #1',
  lines: [
    'I was the one two weeks away for four months. FOUNDR-XI is built inside FOUNDR-XI — its own thesis, market map, interview notes and decision log live in a vault, written by the same ten agents you get.',
    'When a gate was too soft, it failed me first, and I made it harder. That is the whole promise: a bunker that tells its own founder the truth is a bunker that can tell you the truth.',
  ],
};

export interface FooterLink { label: string; href: string; }
export interface FooterCol { head: string; links: FooterLink[]; }
// Every entry must point somewhere real — no placeholder pages.
export const FOOTER_COLS: FooterCol[] = [
  {
    head: 'GAME',
    links: [
      { label: 'Why it works', href: '#why' },
      { label: 'Agents', href: '#agents' },
      { label: 'How it works', href: '#method' },
      { label: 'Roadmap', href: '#roadmap' },
      { label: 'What you own', href: '#vault' },
    ],
  },
  {
    head: 'ACCESS',
    links: [
      { label: 'Pricing', href: '/pricing' },
      { label: 'Questions', href: '#faq' },
      { label: 'Log in', href: '/app' },
      { label: 'Join the bunker', href: '/app' },
    ],
  },
];
